import LibraryClient from '../../client/library';
import { useSelector } from 'react-redux';
import flowRight from 'lodash.flowright';
import URLSearchParams from '@ungap/url-search-params';
import { parseBool, parseJSON, parseURI } from '../../helpers/parsers';
import { WeakType } from '../../typescript-migration';

import { actionTypes } from '../actions/config';
import { MembershipType } from '../../constants/membershipType';

const getConfig = s => s.config;
export const useConfig = () => useSelector(getConfig);

export const defaultState = {
  deviceType: null,
  isAppConfigured: false,
  isVerboseOn: false,
  userId: null,
  token: null,
  softwareNumber: null,
  useMetricUnits: null,
  featureFlags: {},
  clients: {
    libraryClient: null,
  },
  entryPoint: null,
  doEnableScaling: false,
  showFreeLibrary: false,
  isClub: false,
  membershipType: undefined,
  language: 'en',
  isPaused: undefined,
  isEmbedded: false,
  isGuest: false,
  platform: 'android',
};

const getQueryStringOverride = (paramName, configPropName) => {
  const url = new URL(window.location.href.replace('#', '&'));
  const search = new URLSearchParams(url.search);
  const shouldOverride = search.has(paramName);
  if (!shouldOverride) {
    return {};
  }
  const qsVal = search.get(paramName);
  const isParamTrue = parseBool(qsVal) || !qsVal;
  return { [configPropName]: isParamTrue };
};

const formatConfig = unformatted => {
  const transforms = {
    entryPoint: parseURI,
    featureFlags: parseJSON,
    // booleans,
    useMetricUnits: parseBool,
    isVerboseOn: parseBool,
    doEnableScaling: parseBool,
    isPaused: parseBool,
    isClub: parseBool,
    isVault: parseBool,
    isEmbedded: parseBool,
    isGuest: parseBool,
  };
  return Object.keys(transforms).reduce(
    (acc, key) => ({
      ...acc,
      [key]: transforms[key](acc[key]),
    }),
    unformatted,
  );
};

const addDerivedProperties = mergedConfig => {
  let membershipType = mergedConfig.membershipType;
  if (mergedConfig.isClub && !mergedConfig.isPaused) {
    membershipType = MembershipType.Premium;
  } else if (mergedConfig.isPaused) {
    membershipType = MembershipType.Free;
  }
  return {
    ...mergedConfig,
    membershipType,
    isLoggedIn: Boolean(mergedConfig.token),
    showFreeLibrary: Boolean(
      membershipType !== undefined &&
        membershipType?.toLowerCase() === MembershipType.Free,
    ),
  };
};

const initLibraryClient = mergedConfig => ({
  ...mergedConfig,
  clients: {
    libraryClient: new LibraryClient(mergedConfig),
  },
});

export default function config(
  state = defaultState,
  action: { type?: string; config?: WeakType } = {},
) {
  switch (action.type) {
    case actionTypes.UPDATE_CONFIG_VALUES: {
      const verboseOverride = getQueryStringOverride('verbose', 'isVerboseOn');
      const mergedConfig = {
        ...state,
        ...action.config,
        ...verboseOverride,
        isAppConfigured: true,
      };
      return flowRight(
        formatConfig,
        addDerivedProperties,
        initLibraryClient,
      )(mergedConfig);
    }
    default:
      return state;
  }
}
